import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { Card, CardHeader, CardContent, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { ArrowLeft, Check, ChevronLeft, ChevronRight, ClipboardCheck, AlertCircle, Stethoscope, X, Info, Play } from 'lucide-react';
import { SCREENING_SECTIONS } from '../../lib/screeningQuestions';
import ScreeningAssessment from '../../components/assessments/ScreeningAssessment';

export default function PatientAssessmentFlow() {
    const { user } = useAuth();
    const { submitAssessment, patientQuestionnaires } = useData();
    const navigate = useNavigate();
    const location = useLocation();

    const requiredType = location.state?.requiredType;
    
    // Fuzzy matching for screening tools (standardizing on normalized title)
    const normalizedRequiredType = requiredType?.toLowerCase().trim();
    const isScreening = normalizedRequiredType?.includes('child developmental screening') || normalizedRequiredType?.includes('screening');
    
    // Fallback to first questionnaire if not specified
    const matchedForm = patientQuestionnaires.find(q => q.title === (isScreening ? 'Child Developmental Screening' : requiredType)) || patientQuestionnaires[0];
    const questions = matchedForm?.questions || [];

    const DOMAINS = isScreening ? SCREENING_SECTIONS : questions;
    const assessmentTitle = isScreening ? 'Child Developmental Screening' : matchedForm?.title || 'Unknown Assessment';
    const draftKey = `p2g_draft_patient_${user?.patientId || user?.id || 'guest'}_${assessmentTitle.replace(/\s+/g, '_')}`;

    const [currentStep, setCurrentStep] = useState(() => {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
            try {
                return JSON.parse(saved).currentStep || 0;
            } catch (e) { return 0; }
        }
        return 0;
    });

    const [answers, setAnswers] = useState(() => {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
            try {
                return JSON.parse(saved).answers || {};
            } catch (e) { return {}; }
        }
        return {};
    });

    const [remarks, setRemarks] = useState(() => {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
            try {
                return JSON.parse(saved).remarks || {};
            } catch (e) { return {}; }
        }
        return {};
    });

    const [restored, setRestored] = useState(false);

    // Initial restore notification
    useEffect(() => {
        if (Object.keys(answers).length > 0 || currentStep > 0) {
            setRestored(true);
            setTimeout(() => setRestored(false), 3000);
        }
    }, []);

    // Autosave effect
    useEffect(() => {
        if (Object.keys(answers).length > 0 || Object.keys(remarks).length > 0 || currentStep > 0) {
            const draft = {
                answers,
                remarks,
                currentStep,
                lastSaved: new Date().toISOString()
            };
            localStorage.setItem(draftKey, JSON.stringify(draft));
        }
    }, [answers, remarks, currentStep, draftKey]);

    // Safety check for empty domains (Prevents blank/crashing page)
    if (!DOMAINS || DOMAINS.length === 0) {
        return (
            <div className="max-w-xl mx-auto py-20 px-6 text-center">
                <div className="bg-white p-12 rounded-[3rem] shadow-premium border border-slate-50">
                    <div className="h-20 w-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-8">
                        <AlertCircle className="h-10 w-10 text-red-500" />
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 font-outfit mb-4 uppercase tracking-tight">Assessment Protocol Missing</h2>
                    <p className="text-slate-500 mb-10 leading-relaxed font-medium">We couldn't find the questionnaire for <b>"{requiredType || 'Unspecified'}"</b>. Please contact your therapist to re-assign the module.</p>
                    <Button onClick={() => navigate('/patient')} className="h-14 rounded-2xl px-10 bg-slate-900 text-white font-black text-xs uppercase tracking-widest shadow-lg">
                        Go Back To Dashboard
                    </Button>
                </div>
            </div>
        );
    }

    const handleAnswer = (questionId, value) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }));
    };

    const handleSelect = (value) => {
        setAnswers(prev => ({ ...prev, [currentStep]: value }));
    };

    const handleRemarkChange = (e) => {
        setRemarks(prev => ({ ...prev, [currentStep]: e.target.value }));
    };

    const isStepComplete = () => {
        if (isScreening) {
            const section = SCREENING_SECTIONS[currentStep];
            if (!section) return false;
            return section.questions.every(q => {
                if (q.type === 'textarea' || q.type === 'text') return true;
                return answers[q.id] !== undefined;
            });
        }
        return answers[currentStep] !== undefined;
    };

    const handleNext = () => {
        if (currentStep < DOMAINS.length - 1) {
            setCurrentStep(curr => curr + 1);
            window.scrollTo(0, 0);
        } else {
            submitAssessment({
                patientId: user?.patientId || 1,
                patientName: user?.patientName || user?.name || 'Guest',
                type: isScreening ? 'Child Developmental Screening' : matchedForm.title,
                submitterRole: 'Patient',
                answers,
                remarks,
                date: new Date().toISOString()
            });
            localStorage.removeItem(draftKey);
            navigate('/patient');
        }
    };

    const handlePrev = () => { if (currentStep > 0) { setCurrentStep(s => s - 1); window.scrollTo(0, 0); } };

    const progress = ((currentStep + 1) / DOMAINS.length) * 100;

    return (
        <div className="max-w-4xl mx-auto space-y-10 py-12 font-inter px-4 animate-in fade-in duration-500">
            {/* Wizard Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 print:hidden bg-white/80 backdrop-blur-md p-6 rounded-[2.5rem] border border-slate-100 shadow-premium">
                <div className="flex items-center gap-5">
                    <div className="h-14 w-14 rounded-2xl bg-brand-600 flex items-center justify-center text-white shadow-premium">
                        <Stethoscope className="h-7 w-7" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 font-outfit tracking-tight uppercase">
                            {isScreening ? 'Developmental Screening' : matchedForm?.title}
                        </h1>
                        <p className="text-slate-400 font-bold text-[10px] uppercase tracking-[0.2em] mt-1">Stage {currentStep + 1} of {DOMAINS.length}: {isScreening ? DOMAINS[currentStep]?.title : 'Item Assessment'}</p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <div className="text-right">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Completion</p>
                        <p className="text-lg font-black text-brand-600 font-outfit">{Math.round(progress)}%</p>
                    </div>
                    <div className="w-32 h-3 bg-slate-100 rounded-full overflow-hidden shadow-inner border border-slate-200">
                        <div className="h-full bg-brand-600 transition-all duration-500 ease-out" style={{ width: `${progress}%` }}></div>
                    </div>
                </div>
            </div>

            {/* Draft Restored Toast */}
            {restored && (
                <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[200] flex items-center gap-3 px-8 py-4 rounded-3xl shadow-2xl border bg-slate-900 text-white text-xs font-black uppercase tracking-widest animate-in fade-in slide-in-from-bottom-4">
                    <Check className="h-4 w-4 text-brand-400 shrink-0" /> Draft restored from local cloud
                </div>
            )}

            {/* Question Card */}
            <div className="min-h-[500px]">
                {isScreening ? (
                    <ScreeningAssessment 
                        section={SCREENING_SECTIONS[currentStep]} 
                        answers={answers} 
                        onChange={handleAnswer} 
                    />
                ) : (
                    <Card className="border-0 shadow-premium rounded-[3rem] bg-white border border-slate-50 overflow-hidden min-h-[500px] flex flex-col">
                        <CardHeader className="p-10 pb-0 border-b border-slate-50 flex flex-row items-center gap-4">
                            <Badge className="bg-brand-50 text-brand-700 border-brand-100 px-4 py-1.5 uppercase tracking-widest text-[10px] font-black rounded-xl">Current Item</Badge>
                        </CardHeader>
                        
                        <CardContent className="p-10 flex-1 flex flex-col justify-center">
                            <div className="max-w-2xl mx-auto w-full text-center space-y-12">
                                <h2 className="text-3xl sm:text-4xl font-black text-slate-900 leading-tight font-outfit tracking-tight text-balance">
                                    {questions[currentStep]}
                                </h2>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {[
                                        { val: 4, label: "Always / Independent", color: "hover:bg-green-50 hover:border-green-200" },
                                        { val: 3, label: "Often / Needs Reminders", color: "hover:bg-brand-50 hover:border-brand-200" },
                                        { val: 2, label: "Sometimes / Needs Help", color: "hover:bg-amber-50 hover:border-amber-200" },
                                        { val: 1, label: "Rarely / Fully Dependent", color: "hover:bg-red-50 hover:border-red-200" }
                                    ].map(option => (
                                        <button
                                            key={option.val}
                                            onClick={() => handleSelect(option.val)}
                                            className={`group h-24 p-6 border-2 rounded-[2rem] text-left transition-all relative overflow-hidden flex flex-col justify-center ${answers[currentStep] === option.val ? 'border-brand-600 bg-brand-600 text-white shadow-premium' : 'text-slate-600 bg-white border-slate-100 ' + option.color}`}
                                        >
                                            <span className="text-sm font-black uppercase tracking-widest relative z-10">{option.label}</span>
                                            {answers[currentStep] === option.val && (
                                                <div className="absolute right-6 top-1/2 -translate-y-1/2 h-8 w-8 bg-white/20 rounded-full flex items-center justify-center">
                                                    <Check className="h-5 w-5 text-white" />
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>

                                <div className="text-left space-y-4">
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Evidence / Observational Remarks</h4>
                                    <textarea
                                        className="w-full min-h-[120px] rounded-[2rem] border-2 border-slate-50 bg-slate-50 px-8 py-6 text-sm focus:bg-white focus:border-brand-500 outline-none transition-all placeholder:text-slate-300 font-medium"
                                        placeholder="Describe specific instances or context for this rating..."
                                        value={remarks[currentStep] || ''}
                                        onChange={handleRemarkChange}
                                    ></textarea>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Navigation Footer */}
            <div className="flex justify-between items-center bg-white/50 backdrop-blur-sm p-6 rounded-[2.5rem] border border-slate-100 shadow-premium">
                <Button variant="ghost" onClick={() => navigate('/patient')} className="h-14 rounded-2xl px-8 font-black text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-red-500 transition-colors">
                    Terminate <X className="ml-2 h-4 w-4" />
                </Button>
                <div className="flex items-center gap-4">
                    {currentStep > 0 && (
                        <Button variant="outline" onClick={handlePrev} className="h-14 w-14 rounded-2xl border-slate-200 bg-white hover:bg-slate-50 transition-all">
                            <ChevronLeft className="h-5 w-5 text-slate-600" />
                        </Button>
                    )}
                    <Button 
                        onClick={handleNext} 
                        disabled={!isStepComplete()} 
                        className="h-14 rounded-2xl px-12 bg-brand-600 hover:bg-brand-700 text-white font-black text-[10px] uppercase tracking-[0.2em] shadow-premium transition-all disabled:opacity-50"
                    >
                        {currentStep === DOMAINS.length - 1 ? "Submit Evaluation" : "Next Section"} <ChevronRight className="ml-3 h-4 w-4" />
                    </Button>
                </div>
            </div>

            <div className="flex justify-center items-center gap-10 opacity-30 text-slate-400 font-bold text-[10px] uppercase tracking-widest pt-8">
                <span className="flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Real-time Sync</span>
                <span className="flex items-center gap-2"><Info className="h-4 w-4" /> Auto-Save Enabled</span>
                <span className="flex items-center gap-2"><Play className="h-4 w-4" /> Active Session</span>
            </div>
        </div>
    );
}
